import moment from 'moment';
import { isOrderTypeSupported } from './helpers';
import { htmlColorsLowerCase } from 'src/constants/htmlColors';
import { ITEM_TYPE } from 'src/constants/main';
import { ICountry } from 'src/mobx/countriesStore';
import { localeStore } from 'src/mobx/localesStore';
import { IDealItem, IMealItem } from 'src/types/combo';
import { ICaloriesValidation, ILangTextValidation, IPriceValidation, IValidation, TTranslate } from 'src/types/main';
import { DELIVERY_ORDER_TYPE, IProductV4 } from 'src/types/product';
import { formatPhone } from 'src/utils/formatter';
import { defaultDateFormat } from '~/constants/dateTime';

export const emailValidation = (data: string): IValidation => {
  if (!data || !data.length) return { isValid: false, errorText: 'required_field_error' };

  const emailRegExp = new RegExp(
    // eslint-disable-next-line
    /^(([a-zA-Z0-9]+((\.|\-|\_)[a-zA-Z0-9]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z0-9_.-]+\.)+[a-zA-Z]{2,}))$/
  );

  const isValid: boolean = emailRegExp.test(data.toLowerCase());

  return { isValid, errorText: isValid ? undefined : 'toast_email_invalid' };
};

export const floatNumbersValidation = (number: string | number, numberSeparators: number = 1, length?: number): boolean => {
  const floatNumberRegExp = new RegExp(/^[0-9,.]*$/);
  number = String(number).trim();

  if (length && number.length > length) return false;
  if ((number.match(/[,.]/g)?.length || 0) > numberSeparators) return false; // number of separators
  return floatNumberRegExp.test(number);
};

export const numberValidation = (number: string | number): boolean => {
  const numberRegExp = new RegExp(/^[0-9]*$/);
  number = String(number).trim();

  return numberRegExp.test(number);
};

export const phoneValidation = (phone: string): boolean => {
  if (!phone || !phone.length) return false;

  const arabiaMobileRegExp = new RegExp(/^(\+966)(5)[0-9]{8}$/);
  // validate Saudi Arabia phone number
  if (formatPhone(phone).startsWith('+9665')) {
    return arabiaMobileRegExp.test(formatPhone(phone));
  }

  return phone.length >= 9 && phone.length <= 13;
};

export const isValidCountryCode = (phone: string | undefined, countries: ICountry[] | undefined): boolean => {
  if (!phone) return true;

  const countryCodes: string[] = countries?.map((country) => formatPhone(country.isd_code || '', true)) || [];
  if (!countryCodes.length) return false;

  const currentPhoneCode: string = formatPhone(phone || '', true).slice(0, 3);
  return !!countryCodes.find((code) => code === currentPhoneCode || code === currentPhoneCode.slice(0, 2) || code === currentPhoneCode.slice(0, 1));
};

export const dateValidation = (date: string) => {
  const [DD, MM, YYYY] = date.split('/');
  const isValidDay = Number(DD) <= 31 && Number(DD) > 0;
  const isValidMonth = Number(MM) <= 12 && Number(MM) > 0;
  const isValidYear = YYYY ? YYYY.length === 4 && Number(YYYY) > 2017 : false;
  const d = moment(date, defaultDateFormat);
  return isValidDay && isValidMonth && isValidYear && d.isValid() && d.isAfter(moment('31/12/2017', defaultDateFormat)) && d.isBefore(moment());
};

export const emptyStringValidation = (
  string: string = '',
  stringValid: boolean,
  valid: boolean
): [stringValid: boolean, valid: boolean, errorText: string | undefined] => {
  if (!string.trim()) {
    valid = false;
    stringValid = false;
  }

  const errorText: string | undefined = stringValid ? undefined : localeStore.t('required_field_error');

  return [stringValid, valid, errorText];
};

export const emptyNumberValidation = (
  number: string | number = '',
  numberIsValid: boolean,
  isValid: boolean,
  strict?: boolean
): [numberIsValid: boolean, isValid: boolean, errorText: string | undefined] => {
  const errorText: string = localeStore.t('required_field_error');
  let _isValid: boolean = true;
  number = String(number);

  if (strict && !Number(number)) {
    _isValid = false;
  } else if (!number || (!Number(number) && Number(number) !== 0)) {
    _isValid = false;
  }

  return [_isValid ? numberIsValid : false, _isValid ? isValid : false, _isValid ? undefined : errorText];
};

export const emptyNameValidation = (
  name: TTranslate,
  nameValidation: ILangTextValidation,
  valid: boolean
): [nameValidation: ILangTextValidation, valid: boolean] => {
  [nameValidation.enValid, valid, nameValidation.enErrorText] = emptyStringValidation(name.en, nameValidation.enValid, valid);
  [nameValidation.arValid, valid, nameValidation.arErrorText] = emptyStringValidation(name.ar, nameValidation.arValid, valid);

  return [nameValidation, valid];
};

const _getSameNameErrorText = (type: ITEM_TYPE): string => {
  switch (type) {
    case ITEM_TYPE.CATEGORY:
      return localeStore.t('same_category_name_error');
    case ITEM_TYPE.ITEM:
      return localeStore.t('same_item_name_error');
    case ITEM_TYPE.ITEM_SIZE:
      return localeStore.t('same_size_name_error');
    case ITEM_TYPE.MODIFIER:
      return localeStore.t('same_modifier_name_error');
    case ITEM_TYPE.MODIFIER_OPTION:
      return localeStore.t('same_option_name_error');
    case ITEM_TYPE.EXCLUSION:
      return localeStore.t('same_exclusion_name_error');
    case ITEM_TYPE.DEAL_COMBO:
      return localeStore.t('same_combo_offer_name_error');
    case ITEM_TYPE.MEAL_COMBO:
      return localeStore.t('same_combo_meal_name_error');
    case ITEM_TYPE.MEAL_SIZE:
      return localeStore.t('same_size_name_error');
    case ITEM_TYPE.MEAL_GROUP:
      return localeStore.t('same_mc_group_name_error');
    case ITEM_TYPE.MEAL_CHOICE:
      return localeStore.t('same_cmgc_name_error');

    default:
      return '';
  }
};
export const sameNameValidation = (options: {
  isValid?: boolean;
  name: ILangTextValidation;
  type: ITEM_TYPE;
  error?: any;
  itemArray?: any[];
  item?: any;
}): [name: ILangTextValidation, isValid: boolean] => {
  const errorText: string = _getSameNameErrorText(options.type);
  let isValid: boolean = typeof options.isValid === 'boolean' ? options.isValid : true;

  // backend error handle
  if (options.error) {
    if (options.error.response?.data?.type === 'duplicateEnName') {
      isValid = false;
      options.name.enValid = false;
      options.name.enErrorText = errorText;
    }
    if (options.error.response?.data?.type === 'duplicateArName') {
      isValid = false;
      options.name.arValid = false;
      options.name.arErrorText = errorText;
    }

    return [options.name, isValid];
  }

  // frontend error handle
  if (options.itemArray?.length && options.item) {
    if (options.itemArray.some((_item) => _item.id !== options.item.id && _item.name.en.trim() === options.item.name.en.trim())) {
      isValid = false;
      options.name.enValid = false;
      options.name.enErrorText = errorText;
    }
    if (options.itemArray.some((_item) => _item.id !== options.item.id && _item.name.ar.trim() === options.item.name.ar.trim())) {
      isValid = false;
      options.name.arValid = false;
      options.name.arErrorText = errorText;
    }

    return [options.name, isValid];
  }

  return [options.name, isValid];
};

export const caloriesValidation = (
  calories: IProductV4['calories'],
  caloriesValid: ICaloriesValidation,
  valid: boolean
): [caloriesValid: ICaloriesValidation, valid: boolean] => {
  if ((calories !== null && typeof calories !== 'number') || Number(calories) < 0) {
    valid = false;
    caloriesValid.state = false;
    caloriesValid.errorText = localeStore.t('required_field_error');
  } else {
    caloriesValid.state = true;
    caloriesValid.errorText = undefined;
  }

  return [caloriesValid, valid];
};

export const emptyPriceValidation = (
  price: string | number,
  priceValid: IPriceValidation,
  valid: boolean
): [priceValidation: IPriceValidation, valid: boolean] => {
  [priceValid.state, valid, priceValid.errorText] = emptyNumberValidation(price, priceValid.state, valid);

  return [priceValid, valid];
};

export const validateItemsOrderType = (items: IDealItem[] | IMealItem[]): boolean => {
  if (!items.length) return false;

  return items.some((item) => {
    // check if item supports all types
    if (item.order_type === DELIVERY_ORDER_TYPE.ALL) return false;
    // compare with first item, conflict if there any mismatches
    return !isOrderTypeSupported(items[0].order_type, item.order_type);
  });
};

export const maxCharsValidation = (
  value: string | number,
  valueIsValid: boolean,
  isValid: boolean,
  length: number
): [valueIsValid: boolean, isValid: boolean, errorText: string | undefined] => {
  const errorText: string = localeStore.t('max_length_validation', { numberOfChars: length });

  if (!value) return [valueIsValid, isValid, undefined];

  const _isValid: boolean = String(value).length < length;

  return [_isValid ? valueIsValid : false, _isValid ? isValid : false, _isValid ? undefined : errorText];
};

export const minCharsValidation = (
  value: string | number,
  valueIsValid: boolean,
  isValid: boolean,
  length: number
): [valueIsValid: boolean, isValid: boolean, errorText: string | undefined] => {
  const errorText: string = localeStore.t('min_length_validation', { numberOfChars: length });

  if (!value) return [false, false, errorText];

  const _isValid: boolean = String(value).length >= length;

  return [_isValid ? valueIsValid : false, _isValid ? isValid : false, _isValid ? undefined : errorText];
};

export const validationColors = (
  color: string,
  { hex = true, rgb = true, html = true, hsla = true }: { hex?: boolean; rgb?: boolean; html?: boolean; hsla?: boolean }
): IValidation => {
  if (!color) return { isValid: false, errorText: 'required_field_error' };
  color = color.toLowerCase().trim();

  const hexReg = new RegExp('^#([a-fA-F0-9]){3}$|[a-fA-F0-9]{6}$');
  const rgbReg = new RegExp('/rgb((d{1,3}), (d{1,3}), (d{1,3}))/');
  const hslaReg = new RegExp('/(hsla | hsl)((d+),s*([d.]+)%,s*([d.]+)%,s*(d*(?:.d+)?))/ig');

  const isHexValid: boolean = hex ? hexReg.test(color) : true;
  const isRgbValid: boolean = rgb ? rgbReg.test(color) : true;
  const isHtmlValid: boolean = html ? htmlColorsLowerCase.includes(color) : true;
  const isHslaValid: boolean = hsla ? hslaReg.test(color) : true;

  const isValid: boolean = (hex && isHexValid) || (rgb && isRgbValid) || (html && isHtmlValid) || (hsla && isHslaValid);

  // TODO: add color invalid error key
  return { isValid };
};
